import { useEffect, useState, useMemo, useRef } from 'react';
import {
  Search,
  X,
  Shuffle,
  SlidersHorizontal,
  Flame,
  Clock,
  EyeOff,
  RotateCcw,
  Check,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import Skeleton from '@mui/material/Skeleton';
import {
  discoverMedia,
  mediaTitle,
  openDiscoveryDetails,
  type HomeMedia,
  type DiscoveryFilters,
  type MediaKind,
} from '../../metadata/HomeRecommendations.ts';
import { NyomiriCard } from '../components/home/NyomiriCard.tsx';
import { yomiriDatabase } from '../../storage/YomiriDatabase.ts';
import { installedAnimeSources, createAnimeSource } from '../../anime/AnimeRepository.ts';
import { sourceProvider } from '../../core/SourceProvider.ts';
import { sourceHttpClient } from '../../core/SourceHttp.ts';
import { BUILTIN_SOURCES } from '../../repositories/BuiltinCatalog.ts';
import type { AnimeTitle } from '../../anime/types.ts';
import type { YomiriManga } from '../../types/extension.ts';
import './discover.css';

const BUILTIN_ANIME_META: Record<string, { name: string; domain: string; website: string }> = Object.fromEntries(
  BUILTIN_SOURCES.filter(source => source.type === 'anime').map(source => [
    source.id,
    {
      name: source.name,
      domain: (() => {
        try {
          return new URL(source.website).hostname;
        } catch {
          return '';
        }
      })(),
      website: source.website,
    },
  ]),
);

const initialFilters: DiscoveryFilters = {
  search: '',
  format: '',
  status: '',
  sort: 'POPULARITY_DESC',
  year: '',
  season: '',
  score: '',
  genres: [],
};

const GENRES: [string, string][] = [
  ['Action', 'Acción'],
  ['Adventure', 'Aventura'],
  ['Comedy', 'Comedia'],
  ['Drama', 'Drama'],
  ['Fantasy', 'Fantasía'],
  ['Horror', 'Terror'],
  ['Mahou Shoujo', 'Mahō shōjo'],
  ['Mecha', 'Mecha'],
  ['Music', 'Música'],
  ['Mystery', 'Misterio'],
  ['Psychological', 'Psicológico'],
  ['Romance', 'Romance'],
  ['Sci-Fi', 'Ciencia ficción'],
  ['Slice of Life', 'Recuentos de la vida'],
  ['Sports', 'Deportes'],
  ['Supernatural', 'Sobrenatural'],
  ['Thriller', 'Suspense'],
];

interface GenericWorkItem {
  id: string;
  title: string;
  coverUrl?: string;
  sourceId: string;
  sourceName: string;
  subtitle?: string;
  rawManga?: YomiriManga;
}

export function YomiriDiscoverView({
  type = 'ANIME',
  onOpenManga,
}: {
  type?: MediaKind;
  onOpenManga?: (mangaId: string, chapterId?: string, manga?: YomiriManga) => void;
}) {
  const isManga = type === 'MANGA';
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Available installed sources (anime or manga)
  const [installedAnime, setInstalledAnime] = useState<string[]>([]);
  const [installedManga, setInstalledManga] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    let active = true;

    if (isManga) {
      const loadMangaSources = () => {
        const exts = sourceProvider.getEnabledExtensions();
        if (active) {
          setInstalledManga(exts.map(e => ({ id: e.id, name: e.name })));
        }
      };
      loadMangaSources();
      window.addEventListener('yomiri-sources-changed', loadMangaSources);
      return () => {
        active = false;
        window.removeEventListener('yomiri-sources-changed', loadMangaSources);
      };
    } else {
      void installedAnimeSources()
        .then(items => {
          if (active) setInstalledAnime(items);
        })
        .catch(() => { });

      const onSourcesChanged = () => {
        void installedAnimeSources().then(items => {
          if (active) setInstalledAnime(items);
        });
      };
      window.addEventListener('yomiri-sources-changed', onSourcesChanged);
      return () => {
        active = false;
        window.removeEventListener('yomiri-sources-changed', onSourcesChanged);
      };
    }
  }, [isManga]);

  // Selected source: 'all' (AniList) or installed source ID (e.g. 'animeav1', 'mangadex')
  const [selectedSource, setSelectedSource] = useState<string>(() => {
    const paramKey = isManga ? 'source' : 'animeSource';
    const fromUrl = new URLSearchParams(location.search).get(paramKey);
    return fromUrl || 'all';
  });

  // Keep selectedSource in sync if user navigates with back/forward
  useEffect(() => {
    const onPop = () => {
      const paramKey = isManga ? 'source' : 'animeSource';
      const fromUrl = new URLSearchParams(location.search).get(paramKey);
      setSelectedSource(fromUrl || 'all');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [isManga]);

  // Handler to switch source and update URL
  const handleSelectSource = (newSource: string) => {
    setSelectedSource(newSource);
    setPage(1);
    setDirectItems([]);
    setItems([]);
    const params = new URLSearchParams(location.search);
    const paramKey = isManga ? 'source' : 'animeSource';
    if (newSource === 'all') {
      params.delete(paramKey);
    } else {
      params.set(paramKey, newSource);
    }
    history.replaceState(null, '', `?${params}`);
  };

  // Search input & debouncing
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [filters, setFilters] = useState<DiscoveryFilters>(initialFilters);
  const [sourceOrder, setSourceOrder] = useState<'popular' | 'latest'>('popular');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [hide, setHide] = useState(false);
  const [seen, setSeen] = useState<number[]>([]);

  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(true);
  const [error, setError] = useState('');
  const [more, setMore] = useState(false);

  // Result collections
  const [items, setItems] = useState<HomeMedia[]>([]);
  const [directItems, setDirectItems] = useState<GenericWorkItem[]>([]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = query.trim();
      setDebouncedQuery(trimmed);
      setFilters(prev => ({ ...prev, search: trimmed }));
    }, 350);
    return () => clearTimeout(timer);
  }, [query]);

  // Load seen library items
  useEffect(() => {
    const settingKey = isManga ? 'manga_library' : 'anime_library';
    void yomiriDatabase
      .getSetting<{ discoveryId?: number; listStatus?: string }[]>(settingKey, [])
      .then(rows =>
        setSeen(
          rows
            .filter(row => row.listStatus === 'COMPLETED')
            .map(row => row.discoveryId || 0),
        ),
      );
  }, [isManga]);

  // Count active advanced filters
  const activeAdvancedCount = useMemo(() => {
    let count = 0;
    if (filters.score) count++;
    if (filters.year) count++;
    if (filters.season) count++;
    if (filters.genres.length) count += filters.genres.length;
    if (hide) count++;
    return count;
  }, [filters, hide]);

  const resetAllFilters = () => {
    setQuery('');
    setDebouncedQuery('');
    setFilters(initialFilters);
    setHide(false);
    setSourceOrder('popular');
  };

  // Reset page and collections on filter key change
  const filterKey = JSON.stringify([selectedSource, sourceOrder, debouncedQuery, filters]);
  useEffect(() => {
    setPage(1);
    setItems([]);
    setDirectItems([]);
  }, [filterKey]);

  // Data fetching effect
  useEffect(() => {
    let live = true;
    setBusy(true);
    setError('');

    if (selectedSource === 'all') {
      // General Discovery (AniList)
      void discoverMedia(type, filters, page)
        .then(rows => {
          if (!live) return;
          setItems(prev =>
            page === 1 ? rows : [...prev, ...rows.filter(r => !prev.some(p => p.id === r.id))],
          );
          setMore(rows.length === 36);
        })
        .catch(err => {
          if (live) setError(err.message || 'Error al consultar catálogo general');
        })
        .finally(() => {
          if (live) setBusy(false);
        });
    } else if (isManga) {
      // Direct Manga Source Connector
      try {
        const ext = sourceProvider.getExtension(selectedSource);
        if (!ext) throw new Error('Esta fuente de manga no está instalada.');

        const fetchPromise = debouncedQuery
          ? ext.search(debouncedQuery, page)
          : sourceOrder === 'latest' && ext.getLatest
            ? ext.getLatest(page)
            : ext.getPopular(page);

        void fetchPromise
          .then(res => {
            if (!live) return;
            const mangas: YomiriManga[] = res.mangas || [];
            const mapped: GenericWorkItem[] = mangas.map(m => ({
              id: m.id,
              title: m.title,
              coverUrl: m.coverUrl,
              sourceId: selectedSource,
              sourceName: ext.name,
              subtitle:
                m.status === 'COMPLETED'
                  ? 'Finalizado'
                  : m.status === 'ONGOING'
                    ? 'En emisión'
                    : m.genres?.[0] || ext.name,
              rawManga: m,
            }));
            setDirectItems(prev =>
              page === 1 ? mapped : [...prev, ...mapped.filter(t => !prev.some(p => p.id === t.id))],
            );
            setMore(Boolean(res.hasNextPage));
          })
          .catch(err => {
            if (live) setError(err.message || `No se pudo conectar con ${ext.name}`);
          })
          .finally(() => {
            if (live) setBusy(false);
          });
      } catch (err) {
        if (live) {
          setError((err as Error).message);
          setBusy(false);
        }
      }
    } else {
      // Direct Anime Source Connector
      try {
        const source = createAnimeSource(selectedSource, sourceHttpClient);
        const fetchPromise = debouncedQuery
          ? source.search(debouncedQuery, page)
          : sourceOrder === 'latest' && source.getLatest
            ? source.getLatest(page)
            : source.getPopular(page);

        void fetchPromise
          .then(res => {
            if (!live) return;
            const titles: AnimeTitle[] = Array.isArray(res) ? res : res?.titles || [];
            const hasNext: boolean = Array.isArray(res)
              ? res.length >= 20
              : Boolean(res?.hasNextPage);
            const sourceName = BUILTIN_ANIME_META[selectedSource]?.name || selectedSource;
            const mapped: GenericWorkItem[] = titles.map(t => ({
              id: t.id,
              title: t.title,
              coverUrl: t.coverUrl,
              sourceId: selectedSource,
              sourceName,
              subtitle:
                t.status === 'COMPLETED'
                  ? 'Finalizado'
                  : t.status === 'ONGOING'
                    ? 'En emisión'
                    : t.genres?.[0] || 'Anime',
            }));
            setDirectItems(prev =>
              page === 1 ? mapped : [...prev, ...mapped.filter(t => !prev.some(p => p.id === t.id))],
            );
            setMore(hasNext);
          })
          .catch(err => {
            if (live)
              setError(
                err.message ||
                `No se pudo cargar el catálogo de ${BUILTIN_ANIME_META[selectedSource]?.name || selectedSource}`,
              );
          })
          .finally(() => {
            if (live) setBusy(false);
          });
      } catch (err) {
        if (live) {
          setError((err as Error).message);
          setBusy(false);
        }
      }
    }

    return () => {
      live = false;
    };
  }, [type, filterKey, page, isManga]);

  // Change individual filter
  const changeFilter = (name: keyof DiscoveryFilters, value: string) => {
    setPage(1);
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  // Open direct source work title
  const handleOpenDirectWork = (work: GenericWorkItem) => {
    if (isManga) {
      if (onOpenManga) {
        onOpenManga(work.id, undefined, work.rawManga);
      } else {
        const params = new URLSearchParams(location.search);
        params.set('section', 'detail');
        params.set('source', work.sourceId);
        params.set('manga', work.id);
        history.pushState({}, '', `?${params}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
      }
    } else {
      const params = new URLSearchParams(location.search);
      params.set('section', 'anime');
      params.set('animeSource', selectedSource);
      params.set('anime', work.id);
      history.pushState({}, '', `?${params}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  };

  // Shuffle / Sorpréndeme
  const handleShuffle = () => {
    if (selectedSource === 'all') {
      const candidates = items.filter(row => !hide || !seen.includes(row.id));
      if (candidates.length) {
        const randomItem = candidates[Math.floor(Math.random() * candidates.length)];
        openDiscoveryDetails(randomItem);
      }
    } else {
      if (directItems.length) {
        const randomItem = directItems[Math.floor(Math.random() * directItems.length)];
        handleOpenDirectWork(randomItem);
      }
    }
  };

  // TV remote & keyboard navigation helper for the search box
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      // Focus the source select, filter select, or first card
      const target = document.querySelector<HTMLElement>(
        '.nyomiri-source-select, .nyomiri-filter-select, .nyomiri-card',
      );
      target?.focus();
    } else if (e.key === 'Enter') {
      (e.target as HTMLInputElement).blur();
    }
  };

  const shown = items.filter(row => !hide || !seen.includes(row.id));
  const currentSourceName =
    selectedSource === 'all'
      ? 'Todas las fuentes (AniList)'
      : isManga
        ? installedManga.find(s => s.id === selectedSource)?.name || selectedSource
        : BUILTIN_ANIME_META[selectedSource]?.name || selectedSource;

  return (
    <section className="nyomiri-discover" aria-label={isManga ? 'Descubrir manga' : 'Descubrir anime'}>
      {/* Header */}
      <header className="nyomiri-discover-header">
        <div className="nyomiri-discover-title-wrap">
          <h1>{isManga ? 'Manga' : 'Anime'}</h1>
          <p className="nyomiri-discover-subtitle">
            {selectedSource === 'all'
              ? 'Catálogo cinematográfico global y descubrimiento multifuente'
              : `Catálogo directo de ${currentSourceName}`}
          </p>
        </div>
      </header>

      {/* Row 1: Buscador Principal, Selector de Fuente & Sorpréndeme */}
      <div className="nyomiri-discover-search-row">
        <div className="nyomiri-search-input-box">
          <Search size={18} className="nyomiri-search-icon" />
          <input
            ref={searchInputRef}
            aria-label={isManga ? 'Buscar manga por título' : 'Buscar anime por título'}
            placeholder={
              selectedSource === 'all'
                ? `Buscar ${isManga ? 'manga' : 'anime'} por título en catálogo general…`
                : `Buscar en ${currentSourceName}…`
            }
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={handleSearchKeyDown}
          />
          {query.length > 0 && (
            <button
              type="button"
              className="nyomiri-clear-btn"
              aria-label="Limpiar búsqueda"
              onClick={() => {
                setQuery('');
                setDebouncedQuery('');
                searchInputRef.current?.focus();
              }}
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Selector de Fuente integrado en el buscador general */}
        <div className="nyomiri-source-select-wrap">
          <span className="nyomiri-source-badge-dot" />
          <select
            aria-label="Filtrar por fuente"
            value={selectedSource}
            onChange={e => handleSelectSource(e.target.value)}
            className="nyomiri-source-select"
          >
            <option value="all">Todas las fuentes (AniList)</option>
            {isManga ? (
              installedManga.length > 0 && (
                <optgroup label="Fuentes de manga conectadas">
                  {installedManga.map(s => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </optgroup>
              )
            ) : (
              installedAnime.length > 0 && (
                <optgroup label="Fuentes conectadas">
                  {installedAnime.map(id => (
                    <option key={id} value={id}>
                      {BUILTIN_ANIME_META[id]?.name || id}
                    </option>
                  ))}
                </optgroup>
              )
            )}
          </select>
          <ChevronDown size={15} className="nyomiri-source-chevron" />
        </div>

        {/* Sorpréndeme Button */}
        <button
          type="button"
          className="nyomiri-discover-btn btn-accent"
          disabled={busy || (selectedSource === 'all' ? !shown.length : !directItems.length)}
          onClick={handleShuffle}
          aria-label="Elegir una obra al azar"
        >
          <Shuffle size={16} />
          <span>Sorpréndeme</span>
        </button>
      </div>

      {/* Row 2: Filtros Primarios */}
      {selectedSource === 'all' ? (
        <div className="nyomiri-discover-filters-row">
          {/* Formato */}
          <select
            aria-label="Filtrar por formato"
            value={filters.format}
            onChange={e => changeFilter('format', e.target.value)}
            className="nyomiri-filter-select"
          >
            <option value="">Todos los formatos</option>
            {isManga ? (
              <>
                <option value="MANGA">Manga</option>
                <option value="ONE_SHOT">One-shot</option>
                <option value="NOVEL">Novela ligera</option>
              </>
            ) : (
              <>
                <option value="TV">Series TV</option>
                <option value="MOVIE">Películas</option>
                <option value="OVA">OVA</option>
                <option value="ONA">ONA</option>
                <option value="SPECIAL">Especial</option>
                <option value="TV_SHORT">TV corta</option>
              </>
            )}
          </select>

          {/* Estado */}
          <select
            aria-label="Filtrar por estado"
            value={filters.status}
            onChange={e => changeFilter('status', e.target.value)}
            className="nyomiri-filter-select"
          >
            <option value="">Cualquier estado</option>
            <option value="RELEASING">En emisión</option>
            <option value="FINISHED">Finalizado</option>
            <option value="NOT_YET_RELEASED">Próximamente</option>
            <option value="CANCELLED">Cancelado</option>
          </select>

          {/* Ordenación */}
          <select
            aria-label="Ordenar resultados"
            value={filters.sort}
            onChange={e => changeFilter('sort', e.target.value)}
            className="nyomiri-filter-select"
          >
            <option value="POPULARITY_DESC">🔥 Popularidad</option>
            <option value="TRENDING_DESC">📈 Tendencias</option>
            <option value="SCORE_DESC">⭐ Puntuación</option>
            <option value="START_DATE_DESC">🕒 Más recientes</option>
            <option value="TITLE_ROMAJI">🔤 Alfabético</option>
          </select>

          {/* Botón de Filtros Avanzados (Colapsable para no saturar TV) */}
          <button
            type="button"
            className={`nyomiri-filter-btn ${showAdvanced || activeAdvancedCount > 0 ? 'active' : ''}`}
            onClick={() => setShowAdvanced(prev => !prev)}
            aria-expanded={showAdvanced}
            aria-label="Mostrar u ocultar filtros avanzados"
          >
            <SlidersHorizontal size={15} />
            <span>Filtros avanzados</span>
            {activeAdvancedCount > 0 && (
              <span className="nyomiri-badge-count">{activeAdvancedCount}</span>
            )}
            {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>

          {/* Botón para restablecer si hay filtros aplicados */}
          {(query || activeAdvancedCount > 0 || filters.format || filters.status) && (
            <button
              type="button"
              className="nyomiri-filter-btn"
              onClick={resetAllFilters}
              title="Restablecer todos los filtros"
            >
              <RotateCcw size={14} />
              <span>Limpiar</span>
            </button>
          )}
        </div>
      ) : (
        /* Filtros cuando se selecciona una fuente instalada específica */
        <div className="nyomiri-discover-filters-row">
          <button
            type="button"
            className={`nyomiri-filter-btn ${sourceOrder === 'popular' ? 'active' : ''}`}
            onClick={() => setSourceOrder('popular')}
          >
            <Flame size={15} />
            <span>Populares</span>
          </button>
          <button
            type="button"
            className={`nyomiri-filter-btn ${sourceOrder === 'latest' ? 'active' : ''}`}
            onClick={() => setSourceOrder('latest')}
          >
            <Clock size={15} />
            <span>Últimos agregados</span>
          </button>
          {query.length > 0 && (
            <button
              type="button"
              className="nyomiri-filter-btn"
              onClick={() => {
                setQuery('');
                setDebouncedQuery('');
              }}
            >
              <RotateCcw size={14} />
              <span>Limpiar búsqueda</span>
            </button>
          )}
        </div>
      )}

      {/* Row 3: Panel de Filtros Avanzados (Desplegable) */}
      {showAdvanced && selectedSource === 'all' && (
        <div className="nyomiri-advanced-panel">
          <div className="nyomiri-advanced-controls-row">
            {/* Puntuación */}
            <select
              aria-label="Filtrar por puntuación mínima"
              value={filters.score}
              onChange={e => changeFilter('score', e.target.value)}
              className="nyomiri-filter-select"
            >
              <option value="">Cualquier puntuación</option>
              <option value="90">90% o más</option>
              <option value="80">80% o más</option>
              <option value="70">70% o más</option>
              <option value="60">60% o más</option>
            </select>

            {/* Año */}
            <select
              aria-label="Filtrar por año de emisión"
              value={filters.year}
              onChange={e => changeFilter('year', e.target.value)}
              className="nyomiri-filter-select"
            >
              <option value="">Cualquier año</option>
              {Array.from({ length: 45 }, (_, i) => String(new Date().getFullYear() + 1 - i)).map(
                yr => (
                  <option key={yr} value={yr}>
                    {yr}
                  </option>
                ),
              )}
            </select>

            {/* Temporada (solo para anime) */}
            {!isManga && (
              <select
                aria-label="Filtrar por temporada"
                value={filters.season}
                onChange={e => changeFilter('season', e.target.value)}
                className="nyomiri-filter-select"
              >
                <option value="">Cualquier temporada</option>
                <option value="WINTER">Invierno</option>
                <option value="SPRING">Primavera</option>
                <option value="SUMMER">Verano</option>
                <option value="FALL">Otoño</option>
              </select>
            )}

            {/* Ocultar vistos */}
            <button
              type="button"
              className={`nyomiri-filter-btn ${hide ? 'active' : ''}`}
              aria-pressed={hide}
              onClick={() => setHide(prev => !prev)}
            >
              <EyeOff size={15} />
              <span>Ocultar ya vistos</span>
            </button>
          </div>

          {/* Géneros */}
          <div>
            <div className="nyomiri-genres-heading">Filtrar por géneros</div>
            <div className="nyomiri-discover-genres">
              {GENRES.map(([value, label]) => {
                const active = filters.genres.includes(value);
                return (
                  <button
                    key={value}
                    type="button"
                    className="nyomiri-genre-chip"
                    aria-pressed={active}
                    onClick={() => {
                      setPage(1);
                      setFilters(prev => ({
                        ...prev,
                        genres: active
                          ? prev.genres.filter(g => g !== value)
                          : [...prev.genres, value],
                      }));
                    }}
                  >
                    {active && <Check size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />}
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Error alert */}
      {error && (
        <div className="nyomiri-discover-error" role="alert">
          <span>{error}</span>
          <button
            type="button"
            className="nyomiri-discover-btn btn-accent"
            style={{ height: 36, padding: '0 12px', marginLeft: 16 }}
            onClick={() => handleSelectSource('all')}
          >
            Volver a Todas las fuentes
          </button>
        </div>
      )}

      {/* Cards Grid */}
      <div className="nyomiri-discover-grid" aria-busy={busy}>
        {busy && (selectedSource === 'all' ? !shown.length : !directItems.length) ? (
          Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              className="nyomiri-discover-skeleton-card"
              style={{ width: '100%', display: 'flex', flexDirection: 'column' }}
            >
              <Skeleton
                variant="rounded"
                animation="wave"
                sx={{
                  width: '100%',
                  aspectRatio: '2/3',
                  borderRadius: '12px',
                  bgcolor: 'rgba(255,255,255,0.05)',
                }}
              />
              <Skeleton
                variant="text"
                animation="wave"
                sx={{ mt: 1, width: '85%', fontSize: 13, bgcolor: 'rgba(255,255,255,0.05)' }}
              />
              <Skeleton
                variant="text"
                animation="wave"
                sx={{ width: '50%', fontSize: 12, bgcolor: 'rgba(255,255,255,0.05)' }}
              />
            </div>
          ))
        ) : selectedSource === 'all' ? (
          shown.map(item => (
            <NyomiriCard
              key={item.id}
              title={mediaTitle(item)}
              coverUrl={item.coverImage.extraLarge || item.coverImage.large}
              format={item.format || (isManga ? 'MANGA' : 'ANIME')}
              averageScore={item.averageScore}
              subtitle={[item.startDate?.year, item.genres?.[0]].filter(Boolean).join(' · ')}
              onClick={() => openDiscoveryDetails(item)}
            />
          ))
        ) : (
          directItems.map(item => (
            <NyomiriCard
              key={`${selectedSource}:${item.id}`}
              title={item.title}
              coverUrl={item.coverUrl}
              format={item.sourceName || (isManga ? 'Manga' : 'Anime')}
              subtitle={item.subtitle}
              onClick={() => handleOpenDirectWork(item)}
            />
          ))
        )}
      </div>

      {/* Empty State */}
      {!busy &&
        !error &&
        (selectedSource === 'all' ? !shown.length : !directItems.length) && (
          <div className="nyomiri-discover-empty">
            <p>No se encontraron obras con los filtros seleccionados.</p>
            <button
              type="button"
              className="nyomiri-discover-btn"
              onClick={resetAllFilters}
            >
              Restablecer búsqueda y filtros
            </button>
          </div>
        )}

      {/* Load More Button */}
      {more && !error && (
        <div className="nyomiri-load-more-wrap">
          <button
            type="button"
            className="nyomiri-load-more-btn"
            disabled={busy}
            onClick={() => setPage(prev => prev + 1)}
          >
            {busy ? (
              <>
                <Skeleton variant="circular" width={16} height={16} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                <span>Cargando más obras…</span>
              </>
            ) : (
              <span>Cargar más obras</span>
            )}
          </button>
        </div>
      )}
    </section>
  );
}
